import { Fragment } from "react";
import { NavLink } from "react-router-dom";
import { motion } from "framer-motion";
import clsx from "clsx";
import useStore from "@/hooks/useStore";
import PropTypes from "prop-types";
import "./styles.scss";

const FooterRevamp = () => {
  const { acfOptions } = useStore();
  const siteOptions = acfOptions();

  const { footer_info: footerInfo, site_logo: siteLogo } = siteOptions;

  const {
    footer_banner: banner,
    footer_insight: footerInsight,
    copyright,
    motto,
    navigation,
    social_media_title: socialMediaTitle,
    social_media: socialMedia,
  } = footerInfo || {};

  const { footer_banner_text, footer_banner_btn, footer_bg_image } = banner || {};

  const bannerImg = footer_bg_image?.url;
  const { insight_title, insight_sub_title } = footerInsight;

  const navigationColumns = navigation?.length || 0;

  const footerVariants = {
    hidden: { y: "-300px", opacity: 0 },
    visible: { y: "0px", opacity: 1 },
  };

  return (
    <>
      <footer id="site-footer-revamp" className="footer-revamp">
        <motion.div
          className="flex"
          variants={footerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {/* Footer Information */}
          {footerInfo && (
            <div className="flex flex-col w-full gap-16 p-4 lg:p-[60px] lg:gap-36">
              {/* Footer Top */}
              <div className="footer-top-container flex flex-col-reverse gap-8 lg:flex-row">
                <div
                  className={clsx(
                    "footer-top flex flex-col w-full gap-8 lg:grid lg:items-start lg:w-[85%]",
                    {
                      "lg:grid-cols-1": navigationColumns === 1,
                      "lg:grid-cols-2": navigationColumns === 2,
                      "lg:grid-cols-3": navigationColumns === 3,
                      "lg:grid-cols-4": navigationColumns === 4,
                      "lg:grid-cols-5": navigationColumns === 5,
                    }
                  )}
                >
                  {navigation?.length &&
                    navigation.map((column, index) => (
                      <div key={index} className="flex flex-col gap-4 items-start lg:gap-8">
                        {column?.title && (
                          <h2 className="footer-title text-lg font-public text-medium lg:text-2xl">
                            {column.title}
                          </h2>
                        )}

                        {column?.links_items?.length && (
                          <ul className="flex flex-col gap-4 items-start">
                            {column.links_items.map((item, index) => {
                              const imageAlt =
                                item?.image?.alt ||
                                item?.image?.title ||
                                item?.link?.title ||
                                "Nicasource badge";

                              return (
                                <li key={index}>
                                  {item?.item_type === "link" && (
                                    <a
                                      href={item?.link?.url}
                                      title={item?.link?.title}
                                      target={item?.link?.target}
                                      rel="noreferrer"
                                      className="text-medium font-public text-sm font-light transition-color hover:text-primary"
                                    >
                                      {item?.link?.title}
                                    </a>
                                  )}

                                  {item?.item_type === "link_image" &&
                                    (item?.link?.url ? (
                                      <a
                                        href={item?.link?.url}
                                        title={item?.link?.title}
                                        target={item?.link?.target}
                                        rel="noreferrer"
                                      >
                                        <img
                                          src={item?.image?.url}
                                          alt={imageAlt}
                                          className="footer-image"
                                        />
                                      </a>
                                    ) : (
                                      <img
                                        src={item?.image?.url}
                                        alt={imageAlt}
                                        className="footer-image"
                                      />
                                    ))}
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    ))}
                </div>

                <div className="footer-insight w-full lg:w-3/12">
                  <div className="insight">
                    <div className={clsx("title", footerInsight.insight_title_color || "white")}>
                      {insight_title}
                    </div>
                    <div className="sub-title">{insight_sub_title}</div>

                    {/* subscription form */}
                    <form
                      action="https://nicasource.us5.list-manage.com/subscribe/post?u=7fd6a6fdf5822e33a17294382&amp;id=f7dd64fde0&amp;f_id=00d643edf0"
                      method="post"
                      target="_blank"
                      noValidate
                      className="newsletter-form"
                    >
                      <label htmlFor="mce-EMAIL" className="newsletter-label">
                        Email
                      </label>
                      <div className="newsletter-input">
                        <input
                          type="email"
                          name="EMAIL"
                          id="mce-EMAIL"
                          className="newsletter-email"
                          required
                        />
                        <button type="submit" className="newsletter-button">
                          Subscribe
                        </button>
                      </div>
                    </form>
                  </div>

                  <div
                    className="footer-banner"
                    style={bannerImg ? { backgroundImage: `url(${bannerImg})` } : undefined}
                  >
                    <div className="overlay" />
                    {footer_banner_text && <span>{footer_banner_text}</span>}

                    {footer_banner_btn?.url && (
                      <div className="banner-button">
                        <a href={footer_banner_btn.url}>{footer_banner_btn.title}</a>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="footer-info-bottom">
                {/* footer divider */}
                <div className="footer-divider-container">
                  <h2>Nicasource LLC</h2>
                  <div className="footer-divider"></div>
                </div>

                {/* Footer Bottom */}
                <div
                  className={clsx(
                    "footer-bottom flex flex-col gap-4 lg:gap-16 lg:grid lg:grid-cols-3 lg:items-end",
                    {
                      "lg:absolute lg:bottom-14 lg:right-[calc(45%+24px)] lg:left-6 lg:gap-4":
                        !navigationColumns,
                    }
                  )}
                >
                  <div className="flex flex-col items-center gap-6 lg:items-start">
                    {siteLogo && (
                      <NavLink to="/">
                        <img
                          src={`${siteLogo?.url || null}`}
                          alt="Site logo"
                          className="w-full h-auto max-w-[76px]"
                        />
                      </NavLink>
                    )}

                    {motto && <p className="footer-revamp-text text-white">{motto}</p>}
                  </div>

                  {copyright && (
                    <div className="flex flex-col items-center justify-center">
                      <p className="footer-revamp-text text-white">
                        {copyright.replace("%year%", new Date().getFullYear())}
                      </p>
                    </div>
                  )}

                  {socialMedia?.length && (
                    <div className="flex flex-row items-center justify-center gap-4 lg:justify-end">
                      <p className="footer-revamp-text text-white mr-4">{socialMediaTitle}</p>

                      {socialMedia.map(({ link, icon }, index) => (
                        <Fragment key={index}>
                          {link && icon && (
                            <a
                              href={link?.url}
                              title={link?.title}
                              target={link?.target}
                              rel="noreferrer"
                              className="opacity-100 transition-opacity hover:opacity-50"
                            >
                              <img src={icon.url} alt={icon?.alt} className="w-6 h-6" />
                            </a>
                          )}
                        </Fragment>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </footer>
    </>
  );
};

FooterRevamp.propTypes = {
  data: PropTypes.shape({
    acf_fc_layout: PropTypes.string,
    footer_revamp: PropTypes.shape({
      footer_banner: PropTypes.shape({
        footer_banner_text: PropTypes.string,
        footer_banner_btn: PropTypes.shape({
          title: PropTypes.string,
          url: PropTypes.string,
          target: PropTypes.string,
        }),
        footer_bg_image: PropTypes.shape({
          url: PropTypes.string,
          alt: PropTypes.string,
          title: PropTypes.string,
        }),
      }),
    }),
  }),
};

export default FooterRevamp;
